/**
 * Tipagem central da HERO.
 * Mantém compatibilidade com a estrutura atual e adiciona
 * os novos campos visuais usados pelo Hero Manager e pela homepage.
 */

export interface HeroButton {
  text: string;
  link: string;
  type: 'primary' | 'secondary';
}

export interface HeroStat {
  icon: string;
  value: string;
  label: string;
}

export type HeroBackgroundMode = 'default' | 'solid' | 'overlay' | 'gradient';

/**
 * Configurações visuais incrementais da HERO.
 * Todos os campos são opcionais para preservar compatibilidade
 * com registros antigos já existentes no banco.
 */
export interface HeroVisualConfig {
  // Fundo / overlay / sólido / gradiente
  hero_background_mode?: HeroBackgroundMode;

  /**
   * Campos legados já usados no projeto atual.
   * Mantidos para compatibilidade.
   */
  hero_background_color?: string;
  hero_background_opacity?: number;
  hero_background_gradient?: string;

  /**
   * Novo padrão alinhado com as sections.
   */
  hero_solid_color?: string;

  hero_overlay_color?: string;
  hero_overlay_opacity?: number;

  hero_gradient_from?: string;
  hero_gradient_to?: string;
  hero_gradient_direction?: 'to right' | 'to bottom' | '135deg';
  hero_gradient_opacity?: number;

  // Selo superior
  badge_background_color?: string;
  badge_text_color?: string;
  badge_border_color?: string;
  badge_shadow?: boolean;

  // Botão principal
  button_primary_color?: string;
  button_primary_text_color?: string;
  button_primary_border_color?: string;
  button_primary_hover?: string;
  button_primary_gradient?: string;

  // Botão secundário
  button_secondary_color?: string;
  button_secondary_text_color?: string;
  button_secondary_border_color?: string;
  button_secondary_hover?: string;
  button_secondary_gradient?: string;

  // Estilo global dos 4 blocos
  stats_block_background?: string;
  stats_block_opacity?: number;
  stats_block_border?: string;
  stats_block_blur?: number;
  stats_block_text_color?: string;
  stats_block_icon_color?: string;
  stats_block_shadow?: boolean;
}

export interface HeroConfig extends HeroVisualConfig {
  id: string;
  badge: string;
  title: string;
  subtitle: string;
  buttons: HeroButton[];
  stats: HeroStat[];
  backgroundImage: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Defaults visuais compartilhados entre admin, hook e homepage.
 */
export const DEFAULT_HERO_VISUAL_CONFIG: Required<HeroVisualConfig> = {
  hero_background_mode: 'overlay',

  /**
   * Campos legados
   */
  hero_background_color: '#0f172a',
  hero_background_opacity: 0.62,
  hero_background_gradient:
    'linear-gradient(135deg, rgba(15, 61, 62, 0.88) 0%, rgba(15, 23, 42, 0.55) 55%, rgba(245, 124, 0, 0.28) 100%)',

  /**
   * Novo padrão
   */
  hero_solid_color: '#0f172a',

  hero_overlay_color: '#0f172a',
  hero_overlay_opacity: 0.62,

  hero_gradient_from: '#0f3d3e',
  hero_gradient_to: '#f57c00',
  hero_gradient_direction: '135deg',
  hero_gradient_opacity: 1,

  // Selo superior
  badge_background_color: '#F57C00',
  badge_text_color: '#FFFFFF',
  badge_border_color: 'rgba(255,255,255,0.28)',
  badge_shadow: true,

  // Botão principal
  button_primary_color: '#F57C00',
  button_primary_text_color: '#FFFFFF',
  button_primary_border_color: '#F57C00',
  button_primary_hover: '#d66a00',
  button_primary_gradient: '',

  // Botão secundário
  button_secondary_color: 'rgba(255,255,255,0.08)',
  button_secondary_text_color: '#FFFFFF',
  button_secondary_border_color: 'rgba(255,255,255,0.3)',
  button_secondary_hover: 'rgba(255,255,255,0.18)',
  button_secondary_gradient: '',

  // Blocos de estatísticas
  stats_block_background: '#ffffff',
  stats_block_opacity: 0.14,
  stats_block_border: 'rgba(255,255,255,0.22)',
  stats_block_blur: 10,
  stats_block_text_color: '#FFFFFF',
  stats_block_icon_color: '#F57C00',
  stats_block_shadow: true,
};